import { APIOutput, CommonUtils, IFile, S3File } from "@amzn/ask-legal-domain";
import {
    Box,
    Button,
    Checkbox,
    Container,
    FileDropzone,
    FileInput,
    FileTokenGroup,
    Flashbar,
    FlashbarProps,
    Form,
    FormField,
    Header,
    SpaceBetween
} from "@amzn/awsui-components-react";
import * as React from "react";
import { FileUtils } from "../../utils/file-utils";
import { AppContext } from "../../setup/context";

const SUPPORTED_FILE_EXTN = [FileUtils.Extension.JPEG];
const SUPPORTED_FILE_SIZE_IN_MB = 1;
const ERROR_MESSAGE = {
    FILE_TYPE: "File type not supported",
    FILE_SIZE: `File size exceeds ${SUPPORTED_FILE_SIZE_IN_MB} mb`,
    FILE_SIGNATURE: "File does not match the expected file signature",
    UPLOAD_FAILED: "Failed to upload image. Please try again later."
};
export const DEFAULT_INSTANCE_HOME_PAGE_FILE_NAME = "home-page.jpeg";

export interface InstanceHomePageImageUploadProps {
    instanceId: string;
}

export const InstanceHomePageImageUpload: React.FC<InstanceHomePageImageUploadProps> = ({ instanceId }) => {
    const context = React.useContext(AppContext);

    const [isLoading, setIsLoading] = React.useState(false);
    const [flashbarItems, setFlashbarItems] = React.useState<FlashbarProps["items"]>([]);
    const [files, setFiles] = React.useState<File[]>([]);
    const [hasPreviewed, setHasPreviewed] = React.useState(false);
    const [checked, setChecked] = React.useState(false);

    const showError = (message: string) => {
        setFlashbarItems([{ type: "error", content: message }]);
    };

    const clearErrors = () => {
        setFlashbarItems([]);
    };

    const validateFiles = async (files: File[]): Promise<string> => {
        if (!files.length) return "";

        const firstFile = files[0];
        if (!SUPPORTED_FILE_EXTN.includes(FileUtils.getExtension(firstFile.name))) {
            return ERROR_MESSAGE.FILE_TYPE;
        }
        if (firstFile.size > CommonUtils.getValueInBytes(SUPPORTED_FILE_SIZE_IN_MB, "mb")) {
            return ERROR_MESSAGE.FILE_SIZE;
        }
        if (!(await FileUtils.isValidFileSignature(firstFile))) {
            return ERROR_MESSAGE.FILE_SIGNATURE;
        }
        return "";
    };

    const handleFileChange = async (newValue: File[]) => {
        if (isLoading) return;

        setHasPreviewed(false);
        setChecked(false);

        const filesChanged = newValue.slice(0, 1);
        if (!filesChanged.length) {
            setFiles([]);
            clearErrors();
            return;
        }

        const error = await validateFiles(filesChanged);
        if (error) {
            showError(error);
            return;
        }

        clearErrors();
        setFiles(filesChanged);
    };

    const generateObjectId = (file: File): string => {
        const fileUrl = URL.createObjectURL(file);
        const fileUrlSplit = fileUrl.split("/");
        return fileUrlSplit[fileUrlSplit.length - 1];
    };

    const handleOnPreview = () => {
        if (!files.length) return;

        window.open(`#/instance/${instanceId}/preview/${generateObjectId(files[0])}/home`, "_blank");
        setHasPreviewed(true);
    };

    const handleOnUpload = async () => {
        if (!files.length) return;

        try {
            setIsLoading(true);
            clearErrors();

            const fileToUpload = files[0];
            const key: S3File.AskLegalFileKey = {
                prefix: {
                    instanceName: instanceId
                },
                filename: DEFAULT_INSTANCE_HOME_PAGE_FILE_NAME
            };

            const rawOutput = await context.getFileAPI().getPresignedUrl(
                IFile.PresignedUrlInput.create({
                    key,
                    operation: IFile.PresignedOperation.Put,
                    contentType: S3File.ContentType.StaticAssetsImage
                })
            );

            const s3PresignedUrlOutput = APIOutput.fromRaw<IFile.PresignedUrlOutput>(rawOutput.data);
            if (s3PresignedUrlOutput.isErr()) {
                throw new Error(s3PresignedUrlOutput.err.message);
            }

            const uploadViaPresignedUrlRawOutput = await context.getFileAPI().uploadViaPresignedUrl({
                data: fileToUpload,
                presignedUrl: s3PresignedUrlOutput.data.presignedUrl
            });

            if (uploadViaPresignedUrlRawOutput.data.isErr()) {
                throw new Error(uploadViaPresignedUrlRawOutput.data.err.message);
            }

            setFiles([]);
            setHasPreviewed(false);
            window.open(`#/instance/${instanceId}/home`, "_blank");
        } catch (e) {
            console.error(e);
            showError(ERROR_MESSAGE.UPLOAD_FAILED);
        } finally {
            setIsLoading(false);
        }
    };

    const isFileSelected = files.length > 0;

    return (
        <Container
            header={
                <Header variant="h2" description="Feature to upload instance home page background image">
                    Upload Instance Home Page Image
                </Header>
            }
        >
            <SpaceBetween size="m">
                {flashbarItems.length > 0 && <Flashbar items={flashbarItems} />}
                <Form
                    actions={
                        <SpaceBetween size="m">
                            <Button
                                ariaLabel="Preview on home page (opens new tab)"
                                variant="primary"
                                disabled={!isFileSelected}
                                loading={isLoading}
                                onClick={handleOnPreview}
                                iconAlign="right"
                                iconName="external"
                            >
                                Preview
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <FormField label="Background Image Upload">
                        <SpaceBetween size="l">
                            <FileDropzone onChange={({ detail }) => handleFileChange(detail.value)}>
                                <SpaceBetween size="xxs" alignItems="center">
                                    <Box color="inherit">
                                        Drop files here or select from below
                                    </Box>
                                    <FileInput
                                        value={files}
                                        onChange={({ detail }) => handleFileChange(detail.value)}
                                    >
                                        Choose files
                                    </FileInput>
                                    <Box color="inherit" variant="small">
                                        Accepted file types: {SUPPORTED_FILE_EXTN.join(", ")}
                                        <br />
                                        Maximum file size: {SUPPORTED_FILE_SIZE_IN_MB} MB
                                    </Box>
                                </SpaceBetween>
                            </FileDropzone>
                            <FileTokenGroup
                                items={files.map(file => ({ file }))}
                                limit={1}
                                showFileSize
                                showFileLastModified
                                showFileThumbnail
                                alignment="vertical"
                                onDismiss={({ detail }) => {
                                    const newValue = files.filter(
                                        (_, index) => index !== detail.fileIndex
                                    );
                                    handleFileChange(newValue);
                                }}
                                i18nStrings={{
                                    removeFileAriaLabel: () => "Remove file",
                                    limitShowFewer: "Show fewer files",
                                    limitShowMore: "Show more files",
                                    errorIconAriaLabel: "Error",
                                    warningIconAriaLabel: "Warning"
                                }}
                            />
                        </SpaceBetween>
                    </FormField>
                </Form>
                {hasPreviewed && (
                    <Form
                        actions={
                            <SpaceBetween size="m">
                                <Button
                                    variant="primary"
                                    disabled={!isFileSelected || !checked}
                                    onClick={handleOnUpload}
                                    loading={isLoading}
                                >
                                    Publish
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        <FormField
                            label="Terms and conditions"
                            description={
                                <>
                                    <p>
                                        By uploading this image, you confirm that you have the required rights to use this image and that have tested previewing the image.
                                    </p>
                                </>
                            }
                        >
                            <Checkbox
                                disabled={!isFileSelected}
                                checked={checked}
                                onChange={({ detail }) => setChecked(detail.checked)}
                            >
                                I agree to the above terms and conditions
                            </Checkbox>
                        </FormField>
                    </Form>
                )}
            </SpaceBetween>
        </Container>
    );
};